import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Github } from "lucide-react";

/**
 * Compact article card with two variants:
 *  - "home" (default): current small tiles
 *  - "articles": small tiles + subtle hover shadow + GitHub pill
 *
 * Accepts article: {
 *   title, path|href|link, imageSrc|image, summary, tags[], repoUrl, category?
 * }
 */
export default function ArticleCard({ article, variant = "home" }) {
  const img = article.imageSrc || article.image || null;

  // be flexible with link prop names
  const toPath = article.path || article.href || article.link || null;
  const isExternal = !!toPath && /^https?:\/\//i.test(toPath);
  const Wrapper = ({ children, className }) => {
    if (!toPath) return <div className={className}>{children}</div>;
    return isExternal ? (
      <a href={toPath} target="_blank" rel="noopener noreferrer" className={className}>
        {children}
      </a>
    ) : (
      <Link to={toPath} className={className}>
        {children}
      </Link>
    );
  };

  const category = article.category || (Array.isArray(article.tags) && article.tags[0]) || null;

  return (
    <article
      className={[
        "rounded-2xl overflow-hidden border border-slate-800 bg-slate-900 shadow-md shadow-slate-950/30",
        variant === "articles" ? "hover:shadow-lg hover:shadow-slate-950/40 transition" : "",
      ].join(" ")}
    >
      {/* Image */}
      {img && (
        <Wrapper className="relative block">
          <img src={img} alt={article.title} className="w-full h-56 object-cover" />
          {category && (
            <span className="absolute top-3 left-3 inline-flex items-center rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white">
              {category}
            </span>
          )}
          {/* GitHub pill only on the Articles variant */}
          {article.repoUrl && variant === "articles" && (
            <a
              href={article.repoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-lg border border-slate-700/80 bg-slate-900/70 px-2 py-1.5 text-slate-100 hover:bg-slate-800/80"
              aria-label="View repository on GitHub"
              title="View on GitHub"
            >
              <Github className="w-4 h-4" />
              <span className="text-xs hidden sm:inline">GitHub</span>
            </a>
          )}
        </Wrapper>
      )}

      {/* Body */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-white">
          <Wrapper className="hover:underline">{article.title}</Wrapper>
        </h3>

        {article.summary && <p className="mt-2 text-slate-300 text-sm">{article.summary}</p>}

        {Array.isArray(article.tags) && article.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <span
                key={t}
                className="text-xs rounded-full bg-slate-800 px-2 py-1 text-slate-300 border border-slate-700"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex items-center justify-between">
          {toPath && (
            <Wrapper className="inline-flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300 font-medium">
              {variant === "articles" ? "Read" : "Read Article"} <ArrowRight className="w-4 h-4" />
            </Wrapper>
          )}

          {/* In the Home variant, keep a subtle GitHub link in the footer row */}
          {article.repoUrl && variant !== "articles" && (
            <a
              href={article.repoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-slate-300 hover:text-white"
              aria-label="GitHub repository"
              title="View on GitHub"
            >
              <Github className="w-4 h-4" />
              <span className="text-xs hidden sm:inline">GitHub</span>
            </a>
          )}
        </div>
      </div>
    </article>
  );
}
